import React from 'react';

// On-brand, differentiated per-trait colours (terracotta-led, warm/earthy).
export const TRAIT_COLORS = {
  D: '#0076A3', // terracotta
  I: '#E39A3B', // warm amber
  S: '#7FB08A', // sage
  C: '#7E8FB5', // muted slate-blue
};

export const TRAIT_NAMES = { D: 'Dominance', I: 'Influence', S: 'Steadiness', C: 'Conscientiousness' };
const ORDER = ['D', 'I', 'S', 'C'];

// A single 0–100 horizontal bar graph (Graph I / Graph II).
export function BarGraph({ title, subtitle, data = {}, testid }) {
  return (
    <div data-testid={testid} style={{ marginBottom: '1.25rem' }}>
      <div style={{ fontFamily: 'Cabinet Grotesk', fontSize: '0.95rem', fontWeight: 700, color: '#002A38' }}>{title}</div>
      {subtitle && <div style={{ fontSize: '0.75rem', color: '#78716C', marginBottom: '0.6rem' }}>{subtitle}</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: subtitle ? 0 : '0.6rem' }}>
        {ORDER.map((t) => {
          const v = Math.max(0, Math.min(100, data[t] ?? 0));
          return (
            <div key={t} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <div style={{ width: 116, fontSize: '0.75rem', color: '#44403C', flexShrink: 0 }}>
                <span style={{ fontWeight: 700, color: TRAIT_COLORS[t] }}>{t}</span> {TRAIT_NAMES[t]}
              </div>
              <div style={{ flex: 1, height: 14, background: '#F1EDE8', borderRadius: 999, overflow: 'hidden' }}>
                <div
                  data-testid={`${testid}-bar-${t}`}
                  style={{ width: `${v}%`, height: '100%', background: TRAIT_COLORS[t], borderRadius: 999, transition: 'width 0.6s cubic-bezier(0.16,1,0.3,1)' }}
                />
              </div>
              <div style={{ width: 36, textAlign: 'right', fontSize: '0.75rem', fontWeight: 700, color: '#002A38' }}>{Math.round(v)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// A diverging (-100..100) bar graph centred on zero (Graph III / adapted shift).
export function DivergingGraph({ title, subtitle, data = {}, testid }) {
  return (
    <div data-testid={testid} style={{ marginBottom: '1.25rem' }}>
      <div style={{ fontFamily: 'Cabinet Grotesk', fontSize: '0.95rem', fontWeight: 700, color: '#002A38' }}>{title}</div>
      {subtitle && <div style={{ fontSize: '0.75rem', color: '#78716C', marginBottom: '0.6rem' }}>{subtitle}</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: subtitle ? 0 : '0.6rem' }}>
        {ORDER.map((t) => {
          const v = Math.max(-100, Math.min(100, data[t] ?? 0));
          const frac = Math.abs(v) / 100;
          return (
            <div key={t} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <div style={{ width: 116, fontSize: '0.75rem', color: '#44403C', flexShrink: 0 }}>
                <span style={{ fontWeight: 700, color: TRAIT_COLORS[t] }}>{t}</span> {TRAIT_NAMES[t]}
              </div>
              <div style={{ flex: 1, height: 14, background: '#F1EDE8', borderRadius: 4, position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1, background: '#D6CFC6' }} />
                <div
                  data-testid={`${testid}-bar-${t}`}
                  style={{
                    position: 'absolute', top: 0, bottom: 0, background: TRAIT_COLORS[t],
                    width: `${frac * 50}%`,
                    left: v >= 0 ? '50%' : `${50 - frac * 50}%`,
                    transition: 'width 0.6s ease, left 0.6s ease',
                  }}
                />
              </div>
              <div style={{ width: 36, textAlign: 'right', fontSize: '0.75rem', fontWeight: 700, color: v >= 0 ? '#002A38' : '#B45309' }}>
                {v >= 0 ? `+${Math.round(v)}` : Math.round(v)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
